/**
 * Turns an UpChunk `error` event into something we can explain to the
 * uploader. UpChunk only hands back an English sentence, and its read failure
 * says "Try loading from another browser" — advice that is wrong on an iPhone,
 * where every browser runs on WebKit and fails the same way.
 *
 * WebKit cannot reliably read large `File`s: `File.stream()` fails with
 * NotReadableError at 4 GiB and above (muxinc/upchunk#134, WebKit 272600), and
 * iOS blob reads have also failed on smaller files (WebKit 228683,
 * muxinc/upchunk#145). Desktop Chromium/Gecko and Android are not affected, so
 * those get the generic message rather than a misleading browser hint.
 */

export type UploadDevice = 'ios' | 'mac_safari' | 'other'

export type UploadFailure
  = | { kind: 'file_unreadable', device: UploadDevice, overWebKitLimit: boolean }
    | { kind: 'endpoint_unavailable' }
    | { kind: 'server_rejected', status: number }
    | { kind: 'unknown' }

export const WEBKIT_FILE_READ_LIMIT_BYTES = 4 * 1024 * 1024 * 1024

/**
 * The literal fragments of UpChunk's error messages, which are the only signal
 * it exposes (the iterable holding the real error is protected). The spec
 * checks them against the installed library so an upgrade that rewords one
 * fails CI instead of silently falling through to `unknown`.
 */
export const UPCHUNK_MESSAGES = {
  fileUnreadablePrefix: 'Unable to read file of size ',
  fileUnreadableSuffix: ' bytes. Try loading from another browser.',
  endpointPrefix: 'Failed to get endpoint',
  serverRejectedPrefix: 'Server responded with ',
} as const

const NON_SAFARI_MAC_BROWSER = /(?:Chrome|Chromium|Edg|OPR|Firefox)\//

export function detectUploadDevice(env: { userAgent: string, maxTouchPoints: number }): UploadDevice {
  const { userAgent, maxTouchPoints } = env
  if (/iPhone|iPad|iPod/.test(userAgent))
    return 'ios'
  if (/Macintosh/.test(userAgent)) {
    // iPadOS asks for the desktop site by default, so it reports itself as a
    // Mac; touch support is the tell.
    if (maxTouchPoints > 1)
      return 'ios'
    if (/AppleWebKit/.test(userAgent) && !NON_SAFARI_MAC_BROWSER.test(userAgent))
      return 'mac_safari'
  }
  return 'other'
}

export function classifyUploadFailure(
  message: string | undefined,
  context: { fileSize: number, device: UploadDevice },
): UploadFailure {
  if (!message)
    return { kind: 'unknown' }
  if (message.startsWith(UPCHUNK_MESSAGES.fileUnreadablePrefix) && message.endsWith(UPCHUNK_MESSAGES.fileUnreadableSuffix)) {
    return {
      kind: 'file_unreadable',
      device: context.device,
      overWebKitLimit: context.device !== 'other' && context.fileSize >= WEBKIT_FILE_READ_LIMIT_BYTES,
    }
  }
  if (message.startsWith(UPCHUNK_MESSAGES.endpointPrefix))
    return { kind: 'endpoint_unavailable' }
  if (message.startsWith(UPCHUNK_MESSAGES.serverRejectedPrefix)) {
    const status = Number.parseInt(message.slice(UPCHUNK_MESSAGES.serverRejectedPrefix.length), 10)
    if (Number.isInteger(status))
      return { kind: 'server_rejected', status }
  }
  return { kind: 'unknown' }
}
